import {
  Configuration,
  Section,
  StripeRegistryDetails,
} from "../../../resources/modelTypes";
import { RootState } from "../../../resources/reduxTypes";
import { selectSite } from "../../../website/selectors";
import { selectors } from "../../cypressSelectors";
import { FormFieldWrapper } from "../../shared-components/FormFieldWrapper";
import { StripeRegistryProgressBar } from "../../shared-components/StripeRegistryProgressBar";
import { DisplayPhoto } from "../DisplayPhoto";
import CashRegistryDefaultImage from "../assets/cashRegistryDefaultSmall.webp";
import { css } from "@emotion/react";
import {
  Button,
  Modal,
  styleUtils,
  Textarea,
  TextInput,
  theme,
} from "@minted/minted-components";
import React, { memo, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

// Placeholder until a funded-total endpoint exists (same pattern as StripeRegistryManagePreview)
const PLACEHOLDER_FUNDED_CENTS = 0;

enum ContributionStep {
  Details,
  GiverInfo,
  Checkout,
  ThankYou,
}

const formatDollars = (cents: number) =>
  new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits: 0,
    style: "currency",
  }).format(cents / 100);

type StripeContributionModalProps = {
  configuration: Configuration;
  onClose: () => void;
  section: Section;
};

const styles = {
  amountChipRow: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing.x2};
    margin-top: ${theme.spacing.x4};
  `,
  amountChip: (isSelected: boolean) => css`
    border: ${styleUtils.rem(1)} solid
      ${isSelected ? theme.colors.gray600 : theme.colors.borderDefault};
    background-color: ${isSelected ? theme.colors.gray100 : "transparent"};
    cursor: pointer;
    flex: 1;
    min-width: ${styleUtils.rem(72)};
    padding-bottom: ${theme.spacing.x2};
    padding-left: ${theme.spacing.x4};
    padding-right: ${theme.spacing.x4};
    padding-top: ${theme.spacing.x2};
    text-align: center;
    ${theme.typeStyles.bodySmallStyles}
  `,
  description: css`
    ${theme.typeStyles.bodySmallStyles}
    color: ${theme.colors.textSecondary};
    margin-top: ${theme.spacing.x2};
  `,
  fixedAmountLabel: css`
    ${theme.typeStyles.bodyMediumStyles}
    ${theme.typeStyles.bodyBoldStyles}
    margin-top: ${theme.spacing.x4};
  `,
  imageWrapper: css`
    aspect-ratio: 1;
    border-radius: ${styleUtils.rem(4)};
    overflow: hidden;
    width: 100%;

    & img {
      height: 100%;
      object-fit: cover;
      width: 100%;
    }
  `,
  layout: css`
    display: grid;
    gap: ${theme.spacing.x6};
    grid-template-columns: 1fr 1fr;
  `,
  nextButton: css`
    margin-top: ${theme.spacing.x6};
  `,
  otherInput: css`
    margin-top: ${theme.spacing.x4};
  `,
  rightColumn: css`
    display: flex;
    flex-direction: column;
  `,
  subheading: css`
    ${theme.typeStyles.bodySmallStyles}
    color: ${theme.colors.textSecondary};
    margin-top: ${theme.spacing.x2};
  `,
  thankYouWrapper: css`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x6};
    padding-bottom: ${theme.spacing.x6};
    padding-left: 0;
    padding-right: 0;
    padding-top: ${theme.spacing.x6};
    text-align: center;
  `,
  thankYouText: css`
    ${theme.typeStyles.titleMediumStyles}
  `,
  title: css`
    ${theme.typeStyles.titleSmallStyles}
  `,
  formFieldsStack: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x4};
    margin-top: ${theme.spacing.x4};
  `,
};

const StripeContributionModal = ({
  configuration,
  onClose,
  section,
}: StripeContributionModalProps) => {
  const site = useSelector((state: RootState) =>
    selectSite(state.resources.sites),
  );
  const siteDetails = site.siteDetails as
    | { yourName?: string; spouseName?: string }
    | undefined;
  const coupleName = [siteDetails?.yourName, siteDetails?.spouseName]
    .filter(Boolean)
    .join(" & ");

  const details = section.sectionDetails as StripeRegistryDetails;

  const [step, setStep] = useState<ContributionStep>(ContributionStep.Details);
  const [selectedAmountCents, setSelectedAmountCents] = useState<number | null>(
    details.isFixedAmount ? details.giftAmountCents : null,
  );
  const [otherAmountValue, setOtherAmountValue] = useState("");
  const [selectedChip, setSelectedChip] = useState<
    "half" | "full" | "double" | "other" | null
  >(details.isFixedAmount ? "full" : null);
  const [giverName, setGiverName] = useState("");
  const [giverEmail, setGiverEmail] = useState("");
  const [note, setNote] = useState("");

  const halfCents = Math.round(details.giftAmountCents / 2);
  const fullCents = details.giftAmountCents;
  const doubleCents = details.giftAmountCents * 2;

  const quickOptionChips = useMemo(
    () => [
      {
        id: "half" as const,
        cents: halfCents,
        label: formatDollars(halfCents),
      },
      {
        id: "full" as const,
        cents: fullCents,
        label: formatDollars(fullCents),
      },
      {
        id: "double" as const,
        cents: doubleCents,
        label: formatDollars(doubleCents),
      },
    ],
    [halfCents, fullCents, doubleCents],
  );

  const onSelectChip = useCallback(
    (chipId: "half" | "full" | "double") => {
      const chip = quickOptionChips.find((c) => c.id === chipId);
      if (chip) {
        setSelectedChip(chipId);
        setSelectedAmountCents(chip.cents);
        setOtherAmountValue("");
      }
    },
    [quickOptionChips],
  );

  const onOtherAmountChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      if (!/^\d*$/.test(value)) return;
      setOtherAmountValue(value);
      setSelectedChip(value ? "other" : null);
      setSelectedAmountCents(value ? parseInt(value, 10) * 100 : null);
    },
    [],
  );

  const onGiverNameChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
      setGiverName(value),
    [],
  );

  const onGiverEmailChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
      setGiverEmail(value),
    [],
  );

  const onNoteChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) =>
      setNote(value),
    [],
  );

  const isStep1Valid = selectedAmountCents !== null && selectedAmountCents > 0;
  const isStep2Valid =
    giverName.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(giverEmail.trim());

  const renderImage = useCallback(
    () => (
      <div css={styles.imageWrapper}>
        <DisplayPhoto
          configuration={configuration}
          defaultImage={CashRegistryDefaultImage}
          isRegistryPhoto
          section={section}
        />
      </div>
    ),
    [configuration, section],
  );

  const renderStep1 = useCallback(
    () => (
      <div
        css={styles.layout}
        data-cy={selectors.stripeRegistry_contributionModal_step1}
      >
        {renderImage()}
        <div css={styles.rightColumn}>
          <div css={styles.title}>{details.title}</div>
          {details.textDescription && (
            <div css={styles.description}>{details.textDescription}</div>
          )}
          {!details.hideGoal && (
            <StripeRegistryProgressBar
              fundedCents={PLACEHOLDER_FUNDED_CENTS}
              goalAmountCents={details.goalAmountCents}
            />
          )}
          {details.isFixedAmount ? (
            <div css={styles.fixedAmountLabel}>
              Gift amount: {formatDollars(details.giftAmountCents)}
            </div>
          ) : (
            <>
              <div css={styles.amountChipRow}>
                {quickOptionChips.map(({ id, label }) => (
                  <button
                    css={styles.amountChip(selectedChip === id)}
                    data-cy={`${selectors.stripeRegistry_contributionModal_amountChip}_${id}`}
                    key={id}
                    onClick={() => onSelectChip(id)}
                    type="button"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div css={styles.otherInput}>
                <FormFieldWrapper label="Other amount">
                  <TextInput
                    data-cy={
                      selectors.stripeRegistry_contributionModal_amountOtherInput
                    }
                    inputMode="numeric"
                    name="otherAmount"
                    onChange={onOtherAmountChange}
                    pattern="[0-9]*"
                    placeholder="Enter amount"
                    touched
                    value={otherAmountValue}
                  />
                </FormFieldWrapper>
              </div>
            </>
          )}
          <div
            css={styles.nextButton}
            data-cy={selectors.stripeRegistry_contributionModal_nextButton}
          >
            <Button
              disabled={!isStep1Valid}
              expand
              onClick={() => setStep(ContributionStep.GiverInfo)}
              text="Next: Add your details"
              type={Button.types.primary}
            />
          </div>
        </div>
      </div>
    ),
    [
      details,
      isStep1Valid,
      onOtherAmountChange,
      onSelectChip,
      otherAmountValue,
      quickOptionChips,
      renderImage,
      selectedChip,
    ],
  );

  const renderStep2 = useCallback(
    () => (
      <div
        css={styles.layout}
        data-cy={selectors.stripeRegistry_contributionModal_step2}
      >
        {renderImage()}
        <div css={styles.rightColumn}>
          <div css={styles.title}>{details.title}</div>
          <div css={styles.subheading}>
            {`Share your details so ${coupleName} can track your gift`}
          </div>
          <div css={styles.formFieldsStack}>
            <FormFieldWrapper label="Name">
              <TextInput
                data-cy={selectors.stripeRegistry_contributionModal_nameInput}
                name="giverName"
                onChange={onGiverNameChange}
                placeholder="Your name"
                touched
                value={giverName}
              />
            </FormFieldWrapper>
            <FormFieldWrapper label="Email">
              <TextInput
                data-cy={selectors.stripeRegistry_contributionModal_emailInput}
                inputMode="email"
                name="giverEmail"
                onChange={onGiverEmailChange}
                placeholder="Your email"
                touched
                value={giverEmail}
              />
            </FormFieldWrapper>
            <FormFieldWrapper label="Note (optional)">
              <Textarea
                data-cy={selectors.stripeRegistry_contributionModal_noteInput}
                minRows={3}
                name="note"
                onChange={onNoteChange}
                placeholder="Write a personal note"
                value={note}
              />
            </FormFieldWrapper>
          </div>
          <div
            css={styles.nextButton}
            data-cy={selectors.stripeRegistry_contributionModal_nextButton}
          >
            <Button
              disabled={!isStep2Valid}
              expand
              onClick={() => setStep(ContributionStep.Checkout)}
              text={`Next: Give ${formatDollars(selectedAmountCents ?? 0)}`}
              type={Button.types.primary}
            />
          </div>
        </div>
      </div>
    ),
    [
      coupleName,
      details,
      giverEmail,
      giverName,
      isStep2Valid,
      note,
      onGiverEmailChange,
      onGiverNameChange,
      onNoteChange,
      renderImage,
      selectedAmountCents,
    ],
  );

  const renderStep3 = useCallback(
    () => (
      <div data-cy={selectors.stripeRegistry_contributionModal_step3}>
        {/*
         * TODO: Implement embedded Stripe checkout here.
         *
         * Prerequisites:
         *   1. Add `@stripe/react-stripe-js` as a package dependency (via
         *      Dockerfile / make targets — do NOT run yarn directly on the host).
         *   2. Call API.post("stripe/payment-intent/", {
         *        sectionId: section.id,
         *        amountCents: selectedAmountCents,
         *        giverName,
         *        giverEmail,
         *        note,
         *      }) to obtain a `clientSecret`.
         *   3. Use loadStripe(config.get("stripePublishableKey")) → <Elements
         *        stripe={stripePromise} options={{ clientSecret }}>.
         *   4. Render <PaymentElement> with billing_details pre-filled from giverName/giverEmail.
         *   5. On stripe.confirmPayment() success, call setStep(ContributionStep.ThankYou).
         */}
        <p>Payment coming soon.</p>
      </div>
    ),
    [],
  );

  const renderStep4 = useCallback(
    () => (
      <div
        css={styles.thankYouWrapper}
        data-cy={selectors.stripeRegistry_contributionModal_step4}
      >
        <div css={styles.thankYouText}>
          {`Thank you! We've let ${coupleName} know.`}
        </div>
        <div data-cy={selectors.stripeRegistry_contributionModal_doneButton}>
          <Button onClick={onClose} text="Done" type={Button.types.primary} />
        </div>
      </div>
    ),
    [coupleName, onClose],
  );

  const renderContent = useCallback(() => {
    switch (step) {
      case ContributionStep.Details:
        return renderStep1();
      case ContributionStep.GiverInfo:
        return renderStep2();
      case ContributionStep.Checkout:
        return renderStep3();
      case ContributionStep.ThankYou:
        return renderStep4();
    }
  }, [renderStep1, renderStep2, renderStep3, renderStep4, step]);

  return (
    <Modal
      closeOnEsc
      closeOnOverlayClick
      displayCloseButton
      onCloseClick={onClose}
      open
      size={Modal.sizes.medium}
    >
      <Modal.Body>{renderContent()}</Modal.Body>
    </Modal>
  );
};

const MemoizedStripeContributionModal = memo(StripeContributionModal);

export { MemoizedStripeContributionModal as StripeContributionModal };
